/* Native oléyoga countdown adapted for the local offer band.
 * Uses one fixed data-end-date shared by all marquee copies; never a session clock.
 * Preserves the countdown-timer element, init() API and native digit classes.
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.interval = null;
      this.initialized = false;
      this.wasInitialized = false;
      this.transitionTimeouts = new Set();
      this.transitionFrames = new Set();

      // ScrollingBanner calls init() after this component is defined.
      if (!this.closest('.marquee-item')) {
        if (typeof window.initLazyScript === 'function') {
          window.initLazyScript(this, this.init.bind(this));
        } else {
          queueMicrotask(() => this.init());
        }
      }
    }

    connectedCallback() {
      if (this.wasInitialized && !this.initialized) this.init();
    }

    disconnectedCallback() {
      this.stop();
      this.transitionTimeouts.forEach((id) => clearTimeout(id));
      this.transitionFrames.forEach((id) => cancelAnimationFrame(id));
      this.transitionTimeouts.clear();
      this.transitionFrames.clear();
      this.initialized = false;
    }

    init() {
      if (this.initialized || !this.isConnected) return;
      this.endDate = new Date(this.dataset.endDate || '').getTime();
      if (!Number.isFinite(this.endDate)) {
        this.dataset.invalidDate = 'true';
        this.hidden = true;
        return;
      }
      if (this.dataset.invalidDate === 'true') {
        delete this.dataset.invalidDate;
        this.hidden = false;
      }

      this.daysEl = this.querySelector('.js-days .countdown__number-part');
      this.hoursPart1El = this.querySelector('.js-hours .countdown__number-part-1');
      this.hoursPart2El = this.querySelector('.js-hours .countdown__number-part-2');
      this.minsPart1El = this.querySelector('.js-mins .countdown__number-part-1');
      this.minsPart2El = this.querySelector('.js-mins .countdown__number-part-2');
      this.secsPart1El = this.querySelector('.js-secs .countdown__number-part-1');
      this.secsPart2El = this.querySelector('.js-secs .countdown__number-part-2');
      if (![this.daysEl, this.hoursPart1El, this.hoursPart2El, this.minsPart1El,
        this.minsPart2El, this.secsPart1El, this.secsPart2El].every(Boolean)) return;

      this.second = 1000;
      this.minute = 60 * this.second;
      this.hour = 60 * this.minute;
      this.day = 24 * this.hour;
      const duration = getComputedStyle(this).getPropertyValue('--countdown-number-transition-duration');
      this.numberTransitionDurationMS = duration ? CountdownTimer.durationToMS(duration) : 0;
      this.animated = this.numberTransitionDurationMS > 0;

      if (this.animated) {
        this.querySelectorAll('.countdown__number:is(.js-hours, .js-mins, .js-secs) .countdown__number-part').forEach((el) => {
          el.innerHTML = '';
          const current = document.createElement('span');
          current.className = 'countdown__number-part-current';
          current.textContent = '0';
          const previous = document.createElement('span');
          previous.className = 'countdown__number-part-previous';
          previous.textContent = '0';
          el.append(current, previous);
        });
      }

      this.initialized = true;
      this.wasInitialized = true;
      if (this.timer()) this.interval = setInterval(this.timer.bind(this), this.second);
      this.setAttribute('loaded', '');
    }

    stop() {
      if (this.interval !== null) clearInterval(this.interval);
      this.interval = null;
    }

    timer() {
      const timeDiff = this.endDate - Date.now();
      if (!Number.isFinite(timeDiff)) {
        this.stop();
        return false;
      }
      if (timeDiff <= 0) {
        this.expire();
        return false;
      }

      const days = Math.floor(timeDiff / this.day);
      const hours = Math.floor(timeDiff / this.hour);
      const mins = Math.floor(timeDiff / this.minute);
      const secs = Math.floor(timeDiff / this.second);
      this.daysEl.closest('.countdown__item').classList.toggle('countdown__item--is-0', days === 0);
      this.daysEl.classList.toggle('countdown__number-part--is-0', days === 0);
      this.daysEl.textContent = days;

      const displayHours = String(hours - days * 24).padStart(2, '0');
      const displayMins = String(mins - hours * 60).padStart(2, '0');
      const displaySecs = String(secs - mins * 60).padStart(2, '0');
      this.setPartValue(this.hoursPart1El, displayHours[0], true);
      this.setPartValue(this.hoursPart2El, displayHours[1]);
      this.setPartValue(this.minsPart1El, displayMins[0], true);
      this.setPartValue(this.minsPart2El, displayMins[1]);
      this.setPartValue(this.secsPart1El, displaySecs[0], true);
      this.setPartValue(this.secsPart2El, displaySecs[1]);
      this.setAttribute('loaded', '');
      return true;
    }

    expire() {
      this.stop();
      this.daysEl.textContent = '0';
      [this.hoursPart1El, this.hoursPart2El, this.minsPart1El,
        this.minsPart2El, this.secsPart1El, this.secsPart2El].forEach((part) => {
        part.classList.remove('countdown__number-part--start-transition');
        part.classList.add('countdown__number-part--is-0');
        if (this.animated) {
          part.querySelector('.countdown__number-part-current').textContent = '0';
          part.querySelector('.countdown__number-part-previous').textContent = '0';
        } else {
          part.textContent = '0';
        }
      });
      this.dataset.expired = 'true';
      this.setAttribute('loaded', '');
      const band = this.closest('#offers-deadline-band');
      if (band) {
        band.dataset.expired = 'true';
        band.classList.add('is-expired');
        band.hidden = true;
      }
    }

    setPartValue(part, value, setZeroClass) {
      if (setZeroClass) part.classList.toggle('countdown__number-part--is-0', value === '0');
      if (!this.animated) {
        part.textContent = value;
        return;
      }
      const current = part.querySelector('.countdown__number-part-current');
      if (value === current.textContent) return;
      const previous = part.querySelector('.countdown__number-part-previous');
      part.classList.add('countdown__number-part--start-transition');
      previous.textContent = current.textContent;
      current.textContent = value;
      const timeoutId = setTimeout(() => {
        this.transitionTimeouts.delete(timeoutId);
        const frameId = requestAnimationFrame(() => {
          this.transitionFrames.delete(frameId);
          part.classList.remove('countdown__number-part--start-transition');
        });
        this.transitionFrames.add(frameId);
      }, 20);
      this.transitionTimeouts.add(timeoutId);
    }

    static durationToMS(duration) {
      if (duration.indexOf('ms') > 0) return parseInt(duration, 10);
      if (duration.indexOf('s') > 0) return parseFloat(duration) * 1000;
      throw new Error(`Duration unit not recognised: ${duration}`);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
